import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabaseClient'; // Adjust path if needed

// SEO: inject meta tags and structured data
function SEOHead() {
  useEffect(() => {
    document.title = 'Opowiedz Mi – Zachowaj wspomnienia dziadków | Cyfrowa Biografia Rodzinna z AI';

    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    const desc = 'Tomasz – AI biograf – dzwoni do seniora i nagrywa 25-minutowy wywiad o jego życiu. Automatycznie tworzy podsumowanie i biografię PDF. Pierwsza sesja bezpłatna.';
    setMeta('description', desc);
    setMeta('keywords', 'nagrywanie wspomnień dziadków, biografia rodzinna, historia rodziny AI, wywiady z seniorami, pamiętnik cyfrowy, historia życia dziadków');
    setMeta('robots', 'index, follow');

    setMeta('og:title', 'Opowiedz Mi – Cyfrowa Biografia Rodzinna z AI', true);
    setMeta('og:description', desc, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', 'https://opowiedz-mi.pl', true);
    setMeta('og:image', 'https://opowiedz-mi.pl/og-image.jpg', true);
    setMeta('og:locale', 'pl_PL', true);
    setMeta('og:site_name', 'Opowiedz Mi', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Opowiedz Mi – Biografia rodzinna z AI');
    setMeta('twitter:description', desc);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://opowiedz-mi.pl';

    // Schema.org – SoftwareApplication
    const schemaApp = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': 'Opowiedz Mi',
      'description': 'Aplikacja do nagrywania historii życia seniorów przy pomocy AI.',
      'url': 'https://opowiedz-mi.pl',
      'applicationCategory': 'LifestyleApplication',
      'operatingSystem': 'Web',
      'inLanguage': 'pl',
      'offers': [
        { '@type': 'Offer', 'name': 'Darmowy', 'price': '0', 'priceCurrency': 'PLN' },
        { '@type': 'Offer', 'name': 'Subskrypcja', 'price': '39', 'priceCurrency': 'PLN' },
        { '@type': 'Offer', 'name': 'Standard', 'price': '99', 'priceCurrency': 'PLN' },
        { '@type': 'Offer', 'name': 'Premium', 'price': '229', 'priceCurrency': 'PLN' }
      ],
    };

    const injectSchema = (id, data) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
      el.textContent = JSON.stringify(data);
    };
    injectSchema('schema-app', schemaApp);
  }, []);

  return null;
}

const LandingPage = () => {
  // Form State
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // FAQ State
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Handle Form Submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    try {
      const { error: supabaseError } = await supabase
        .from('subscribers')
        .insert([{
          email,
          discount_code: 'RODZINA20',
          status: 'active',
          source: 'landing-page',
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
          referrer: document.referrer || null
        }])
        .select();

      if (supabaseError) {
        setFormError(supabaseError.code === '23505'
          ? 'Ten email jest już zapisany na liście!'
          : 'Wystąpił błąd. Spróbuj ponownie.');
        setIsSubmitting(false);
        return;
      }

      setIsSuccess(true);
      if (window.gtag) window.gtag('event', 'generate_lead', { value: 500, currency: 'PLN' });
      
      // Reset form after delay
      setTimeout(() => { 
        setIsSuccess(false); 
        setEmail(''); 
      }, 6000);

    } catch {
      setFormError('Nie udało się połączyć z serwerem. Sprawdź połączenie internetowe.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Initial Data Arrays
  const steps = [
    { num: '01', title: 'Tworzysz profil seniora', desc: 'Dodajesz imię, kilka informacji o bliskiej osobie i wybierasz temat rozmowy – dzieciństwo, młodość, dorosłość lub dziedzictwo.' },
    { num: '02', title: 'Tomasz dzwoni i rozmawia', desc: 'AI biograf dzwoni na telefon seniora i przeprowadza 25-minutowy, naturalny wywiad. Żadnej techniki po stronie seniora.' },
    { num: '03', title: 'Dostajesz gotowy rozdział', desc: 'Po sesji AI generuje podsumowanie w stylu pamiętnika, wyciąga fakty, cytaty i oś czasu. Możesz pobrać PDF dla całej rodziny.' }
  ];

  const plans = [
    { name: 'Darmowy', price: '0', period: 'zł', target: 'Na próbę – jedna rozmowa', featured: false, features: ['1 sesja (25 min)', 'Podsumowanie AI', 'Transkrypt rozmowy'], noFeatures: ['Eksport PDF', 'Pytania od rodziny'] },
    { name: 'Subskrypcja', price: '39', period: 'zł/mies.', target: 'Dla tych którzy nagrywają regularnie', featured: true, features: ['2 sesje miesięcznie', 'Podsumowanie AI', 'Eksport PDF z biografią', 'Pytania od rodziny', 'HD głos Tomasza'], noFeatures: [] },
    { name: 'Standard', price: '99', period: 'zł', target: 'Pełna biografia – jednorazowo', featured: false, features: ['5 sesji', 'Podsumowanie AI', 'Eksport PDF z biografią', 'Pytania od rodziny'], noFeatures: ['HD głos Tomasza'] },
    { name: 'Premium', price: '229', period: 'zł', target: 'Wywiad rzeka – kompletna historia', featured: false, features: ['12 sesji', 'Podsumowanie AI', 'Eksport PDF z biografią', 'Pytania od rodziny', 'HD głos Tomasza'], noFeatures: [] }
  ];

  const faqs = [
    { q: 'Jak działa nagrywanie wspomnień dziadków przez AI?', a: 'AI o imieniu Tomasz prowadzi naturalną, 25-minutową rozmowę przez telefon. Zadaje pytania o dzieciństwo, młodość, dorosłość i dziedzictwo – po polsku, bez technologicznych barier. Po każdej sesji generuje podsumowanie i wyciąga kluczowe fakty, osoby i miejsca.' },
    { q: 'Czy senior musi umieć obsługiwać smartfona?', a: 'Nie. Tomasz dzwoni na zwykły telefon – stacjonarny lub komórkowy. Senior potrzebuje tylko odebrać połączenie. Żadnej aplikacji, żadnych kodów, żadnego logowania po stronie seniora.' },
    { q: 'Ile trwa jedna sesja nagraniowa?', a: 'Dokładnie 25 minut. Tomasz sam pilnuje czasu – przy 20. minucie zadaje pytanie podsumowujące, przy 23. daje czas na ostatnie słowa, przy 25. naturalnie kończy rozmowę. Możesz zaplanować kolejną sesję kiedy chcesz.' },
    { q: 'Czym różni się Subskrypcja od Standard?', a: 'Standard to jednorazowy zakup 5 sesji – płacisz raz, masz historię na zawsze. Subskrypcja to 2 nowe sesje każdego miesiąca – idealna jeśli chcesz, żeby senior regularnie opowiadał o kolejnych etapach życia.' },
    { q: 'Co zawiera biografia rodzinna w PDF?', a: 'Cała historia seniora w jednym pliku: transkrypcje wszystkich rozmów, podsumowania AI dla każdego rozdziału, pogrupowane według etapów życia (Dzieciństwo, Młodość, Dorosłość, Dziedzictwo). Gotowy do druku lub udostępnienia rodzinie.' }
  ];

  return (
    <>
      <SEOHead />

      {/* Navigation */}
      <nav aria-label="Nawigacja główna" className="fixed top-0 left-0 right-0 z-[100] bg-cream/95 backdrop-blur-md border-b border-pageBorder px-8">
        <div className="max-w-[1100px] mx-auto flex items-center justify-between h-16">
          <a href="/" className="font-playfair text-xl font-bold text-brown tracking-tight" aria-label="Opowiedz Mi – strona główna">
            Opowiedz<span className="text-gold">Mi</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#jak-to-dziala" className="text-brown-mid text-sm font-lora hover:text-brown transition-colors">Jak to działa</a>
            <a href="#przyklad" className="text-brown-mid text-sm font-lora hover:text-brown transition-colors">Przykład</a>
            <a href="#cennik" className="text-brown-mid text-sm font-lora hover:text-brown transition-colors">Cennik</a>
            <a href="#faq" className="text-brown-mid text-sm font-lora hover:text-brown transition-colors">FAQ</a>
            <a href="https://app.opowiedz-mi.pl/register" className="bg-brown hover:bg-brown-light text-cream px-5 py-2 rounded-lg text-sm transition-colors">
              Wypróbuj za darmo
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <div className="min-h-screen grid md:grid-cols-2 items-center gap-16 max-w-[1100px] mx-auto px-8 pt-32 pb-16">
          <div className="animate-fadeUp">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-gold mb-5 font-lora">AI Biograf Rodzinny</p>
            <h1 className="font-playfair text-[clamp(2.5rem,5vw,4rem)] font-black leading-[1.1] tracking-tight mb-6">
              Zanim <em className="italic text-brown-light">zabraknie</em><br />czasu na pytania
            </h1>
            <p className="text-lg text-brown-mid max-w-[480px] leading-relaxed mb-10">
              Tomasz – nasz AI biograf – przeprowadza rozmowę z Twoim bliskim przez telefon. Nagrywa historię życia. Tworzy pamiętnik dla całej rodziny.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a href="https://app.opowiedz-mi.pl/register" className="inline-flex items-center gap-2 bg-brown text-cream px-8 py-3.5 rounded-xl font-lora text-base border-2 border-brown hover:bg-brown-light hover:border-brown-light transition-all">
                Zacznij bezpłatnie →
              </a>
              <a href="#przyklad" className="inline-flex items-center gap-2 bg-transparent text-brown px-8 py-3.5 rounded-xl font-lora text-base border-2 border-pageBorder hover:border-brown hover:bg-cream-dark transition-all ml-0 md:ml-4">
                Zobacz przykład
              </a>
            </div>
            <p className="text-sm text-brown-mid mt-4 italic">Pierwsza sesja bezpłatna · Bez karty kredytowej · Po polsku</p>
          </div>

          <div className="hidden md:block animate-fadeUpDelayed relative" aria-hidden="true">
            <div className="bg-white rounded-2xl border border-pageBorder p-8 shadow-[0_20px_60px_rgba(28,25,23,0.12)] relative">
              <span className="text-[4rem] leading-none text-amber absolute -top-6 left-6 font-serif">❝</span>
              <p className="font-playfair text-lg italic leading-relaxed text-brown mb-6 mt-2">
                "Pamiętam jak tata robił drewniane sanki w warsztacie. Śnieg był taki prawdziwy wtedy, do kolan. Mama czekała z gorącą herbatą..."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cream-dark flex items-center justify-center text-base border-2 border-pageBorder">👵</div>
                <div>
                  <div className="text-sm font-semibold text-brown">Helena, 81 lat</div>
                  <div className="text-xs text-brown-mid">Nagrała 4 rozdziały biografii</div>
                </div>
              </div>
            </div>
            
            <div className="bg-brown text-cream rounded-xl border border-pageBorder p-4 mt-3 ml-8">
              <div className="text-[0.65rem] font-bold tracking-widest uppercase mb-1.5 text-amber">Tomasz (AI Biograf)</div>
              <p className="text-sm leading-relaxed text-cream/90">To piękne wspomnienie. A czy pamięta Pani jakiś szczególny wieczór wigilijny z tamtych lat?</p>
            </div>
            
            <div className="bg-cream-dark rounded-xl border border-pageBorder p-4 mt-3 mr-8">
              <div className="text-[0.65rem] font-bold tracking-widest uppercase mb-1.5 text-brown-light">Helena</div>
              <p className="text-sm leading-relaxed text-brown">O, oczywiście! W każde Boże Narodzenie tata wycinał gwiazdki z papieru...</p>
            </div>
          </div>
        </div>
      </main>

      {/* Stats Section */}
      <div className="bg-brown text-cream py-12 px-8" role="region" aria-label="Kluczowe informacje">
        <div className="max-w-[1100px] mx-auto grid md:grid-cols-3 gap-8 text-center">
          <div><div className="font-playfair text-4xl font-bold text-amber leading-none">25 min</div><div className="text-sm text-cream/70 mt-2">tyle trwa każda sesja nagrania</div></div>
          <div><div className="font-playfair text-4xl font-bold text-amber leading-none">4</div><div className="text-sm text-cream/70 mt-2">rozdziały biografii do nagrania</div></div>
          <div><div className="font-playfair text-4xl font-bold text-amber leading-none">100%</div><div className="text-sm text-cream/70 mt-2">po polsku – Tomasz nie zmienia języka</div></div>
        </div>
      </div>

      {/* Problem Section */}
      <section className="bg-cream-dark border-y border-pageBorder py-24 px-8" aria-labelledby="problem-title">
        <div className="max-w-[1100px] mx-auto">
          <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block">Dlaczego to ważne</span>
          <div className="grid md:grid-cols-2 gap-16 items-center mt-8">
            <div>
              <blockquote className="font-playfair text-[1.75rem] italic leading-relaxed text-brown border-l-4 border-amber pl-6">
                "Żałuję, że nie zapytałem dziadka o wojnę. Teraz już nie mogę."
              </blockquote>
              <p className="mt-6 text-brown-mid italic text-[0.95rem]">
                Każdego roku wraz z naszymi bliskimi odchodzą tysiące niepowtarzalnych historii. Pytania, które chcieliśmy zadać, zostają bez odpowiedzi.
              </p>
            </div>
            <div className="flex flex-col gap-5">
              <div className="flex gap-4 items-start">
                <div className="w-9 h-9 bg-brown rounded-lg flex items-center justify-center text-amber text-base shrink-0" aria-hidden="true">📞</div>
                <div><h4 className="text-base font-semibold mb-1 text-brown">Senior nie musi nic instalować</h4><p className="text-sm text-brown-mid leading-relaxed">Tomasz dzwoni na zwykły telefon. Wystarczy odebrać.</p></div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-9 h-9 bg-brown rounded-lg flex items-center justify-center text-amber text-base shrink-0" aria-hidden="true">🎙️</div>
                <div><h4 className="text-base font-semibold mb-1 text-brown">Naturalna rozmowa, nie ankieta</h4><p className="text-sm text-brown-mid leading-relaxed">AI zadaje pytania jak uważny słuchacz, reaguje na emocje, nie czyta z listy.</p></div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-9 h-9 bg-brown rounded-lg flex items-center justify-center text-amber text-base shrink-0" aria-hidden="true">📖</div>
                <div><h4 className="text-base font-semibold mb-1 text-brown">Gotowy pamiętnik dla rodziny</h4><p className="text-sm text-brown-mid leading-relaxed">Każda sesja to podsumowanie AI, transkrypt i piękne PDF do druku.</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-white border-y border-pageBorder py-24 px-8" id="jak-to-dziala" aria-labelledby="how-title">
        <div className="max-w-[1100px] mx-auto">
          <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block">Jak to działa</span>
          <h2 id="how-title" className="font-playfair text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.2] mb-4">Trzy kroki do rodzinnej historii</h2>
          
          <ol className="grid md:grid-cols-3 gap-12 mt-16 list-none">
            {steps.map((step, idx) => (
              <li key={idx} className="relative pt-4">
                <span className="font-playfair text-[5rem] font-black text-cream-dark leading-none mb-[-1rem] block" aria-hidden="true">{step.num}</span>
                <h3 className="font-playfair text-[1.35rem] font-bold mb-3 text-brown">{step.title}</h3>
                <p className="text-[0.95rem] text-brown-mid leading-relaxed">{step.desc}</p>
                {idx !== steps.length - 1 && <div className="hidden md:block absolute top-14 -right-6 w-12 h-[1px] bg-pageBorder" aria-hidden="true"></div>}
              </li>
            ))}
          </ol>
        </div>
      </section>

     {/* Demo Section */}
     <section id="przyklad" className="py-24 px-8 bg-cream" aria-labelledby="demo-title">
        <div className="max-w-[1100px] mx-auto">
          <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block">Przykładowa rozmowa</span>
          <div className="grid md:grid-cols-2 gap-16 items-center mt-8">
            
            <div className="bg-white rounded-2xl border border-pageBorder overflow-hidden shadow-[0_8px_40px_rgba(28,25,23,0.08)]">
              <div className="bg-brown p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber flex items-center justify-center text-sm font-bold text-brown shrink-0" aria-hidden="true">T</div>
                <div>
                  <div className="text-cream text-sm font-semibold">Tomasz – Biograf AI</div>
                  <div className="text-cream/60 text-xs">Rozmowa w toku · Dzieciństwo</div>
                </div>
              </div>
              
              {/* Removed max-h-[380px] and overflow-hidden to prevent text cut-off */}
              <div className="p-5 flex flex-col gap-3.5" role="log" aria-label="Przykładowa rozmowa">
                <div className="max-w-[85%] self-start">
                  <div className="p-3 bg-cream-dark text-brown rounded-xl rounded-bl-sm text-sm leading-relaxed">Dzień dobry, tu Tomasz. Bardzo się cieszę, że możemy dziś porozmawiać. Czy dobrze mnie Pani słyszy?</div>
                  <div className="text-[0.65rem] text-brown-mid mt-1 px-1">Tomasz</div>
                </div>
                <div className="max-w-[85%] self-end">
                  <div className="p-3 bg-brown text-cream rounded-xl rounded-br-sm text-sm leading-relaxed">Tak, tak, słyszę doskonale.</div>
                  <div className="text-[0.65rem] text-brown-mid mt-1 px-1 text-right">Helena</div>
                </div>
                <div className="max-w-[85%] self-start">
                  <div className="p-3 bg-cream-dark text-brown rounded-xl rounded-bl-sm text-sm leading-relaxed">Wspaniale. Chciałbym zacząć od czegoś przyjemnego – czy może mi Pani opowiedzieć o swoim pierwszym domu? Jak on wyglądał, jak pachniał?</div>
                  <div className="text-[0.65rem] text-brown-mid mt-1 px-1">Tomasz</div>
                </div>
                <div className="max-w-[85%] self-end">
                  <div className="p-3 bg-brown text-cream rounded-xl rounded-br-sm text-sm leading-relaxed">Ojej, to już dawne czasy... Mieszkaliśmy w drewnianym domu, we wsi koło Tarnowa. Pamiętam ten zapach – chleb który mama piekła co piątek...</div>
                  <div className="text-[0.65rem] text-brown-mid mt-1 px-1 text-right">Helena</div>
                </div>
                <div className="max-w-[85%] self-start">
                  <div className="p-3 bg-cream-dark text-brown rounded-xl rounded-bl-sm text-sm leading-relaxed">To piękne. Zapach świeżego chleba jest chyba jednym z tych wspomnień, które zostają na całe życie. A czy pamięta Pani rodzeństwo?</div>
                  <div className="text-[0.65rem] text-brown-mid mt-1 px-1">Tomasz</div>
                </div>
                <div className="max-w-[85%] self-end">
                  <div className="p-3 bg-brown text-cream rounded-xl rounded-br-sm text-sm leading-relaxed">Miałam starszą siostrę Zofię. Razem zbierałyśmy jagody w lesie za wsią, latem prawie codziennie...</div>
                  <div className="text-[0.65rem] text-brown-mid mt-1 px-1 text-right">Helena</div>
                </div>
              </div>
            </div>

            <div>
              <h2 id="demo-title" className="font-playfair text-[1.75rem] font-bold leading-[1.3] mb-4 text-brown">Tomasz słucha jak dobry wnuk.</h2>
              <p className="text-base text-brown-mid leading-relaxed mb-4">Nie odczytuje pytań z listy. Reaguje na każdą historię z empatią, dopytuje o szczegóły, pamiętuje co już zostało powiedziane.</p>
              <p className="text-base text-brown-mid leading-relaxed mb-4">Rozmowy są prowadzone po polsku, w tempie dostosowanym do seniora. Jeśli ktoś słabo słyszy – Tomasz mówi wolniej i wyraźniej.</p>
              <ul className="list-none mt-6 space-y-2">
                {['Połączenie na zwykły telefon stacjonarny lub komórkowy', 'Tomasz pamięta poprzednie rozmowy i nie powtarza pytań', 'Omija tematy które wolisz zachować dla siebie', 'Podsumowanie gotowe kilka minut po rozmowie'].map((feat, i) => (
                  <li key={i} className="flex items-start gap-3 py-1 text-[0.95rem] text-brown-mid"><span className="text-gold font-bold shrink-0">✓</span>{feat}</li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="cennik" className="bg-cream-dark border-y border-pageBorder py-24 px-8" aria-labelledby="pricing-title">
        <div className="max-w-[1100px] mx-auto">
          <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block">Cennik</span>
          <h2 id="pricing-title" className="font-playfair text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.2] mb-4 text-brown">Zacznij za darmo</h2>
          <p className="text-[1.05rem] text-brown-mid max-w-[560px] leading-relaxed">Pierwsza sesja jest bezpłatna – żadnych zobowiązań. Zapłać tylko jeśli chcesz nagrać więcej.</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {plans.map((plan, idx) => (
              <article key={idx} className={`bg-white rounded-2xl p-8 transition-transform hover:-translate-y-1 relative ${plan.featured ? 'border-2 border-brown' : 'border border-pageBorder'}`}>
                {plan.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brown text-amber text-[0.7rem] font-bold tracking-widest uppercase py-1 px-3 rounded-full whitespace-nowrap">Najczęściej wybierany</div>}
                <div className="text-[0.75rem] font-bold tracking-[0.15em] uppercase text-gold mb-2">{plan.name}</div>
                <div className="font-playfair text-[2rem] font-bold text-brown leading-none mb-1">{plan.price} <span className="text-base font-normal text-brown-mid">{plan.period}</span></div>
                <div className="text-[0.8rem] text-brown-mid mb-6 pb-6 border-b border-pageBorder italic">{plan.target}</div>
                
                <ul className="list-none flex flex-col gap-2.5 mb-7">
                  {plan.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-[0.85rem] text-brown-mid"><span className="text-gold text-[0.8rem] font-bold shrink-0">✓</span>{f}</li>)}
                  {plan.noFeatures.map((f, i) => <li key={i} className="flex items-center gap-2 text-[0.85rem] text-brown-mid opacity-50"><span className="text-pageBorder text-[0.8rem] font-bold shrink-0">–</span>{f}</li>)}
                </ul>
                
                <a href="https://app.opowiedz-mi.pl/register" className={`block text-center py-3 rounded-lg font-lora text-[0.9rem] border-[1.5px] transition-all ${plan.featured ? 'bg-brown text-cream border-brown hover:bg-brown-light' : 'bg-transparent text-brown border-pageBorder hover:border-brown hover:bg-cream-dark'}`}>
                  {plan.price === '0' ? 'Wypróbuj za darmo' : 'Wybierz plan'}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white border-b border-pageBorder py-24 px-8" aria-labelledby="testi-title">
        <div className="max-w-[1100px] mx-auto">
          <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block">Opinie</span>
          <h2 id="testi-title" className="font-playfair text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.2] mb-4 text-brown">Co mówią opiekunowie</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <div className="p-7 bg-cream rounded-xl border border-pageBorder">
              <p className="text-[0.95rem] leading-relaxed text-brown italic mb-5">"Mama jest bardzo nieśmiała i nigdy nie lubiła mówić o przeszłości. Z Tomaszem rozmawiała przez 40 minut i płakała ze wzruszenia. Nigdy jej taką nie widziałam."</p>
              <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-cream-dark border border-pageBorder flex items-center justify-center text-[0.8rem] font-bold text-brown" aria-hidden="true">AK</div><div><div className="text-[0.85rem] font-semibold text-brown">Anna K.</div><div className="text-[0.75rem] text-brown-mid">Kraków · córka Heleny, 81 l.</div></div></div>
            </div>
            <div className="p-7 bg-cream rounded-xl border border-pageBorder">
              <p className="text-[0.95rem] leading-relaxed text-brown italic mb-5">"Tata mieszka 300 km od nas. Dzięki Opowiedz Mi mamy teraz regularny kontakt – każda sesja to potem rozmowa telefoniczna o tym co powiedział."</p>
              <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-cream-dark border border-pageBorder flex items-center justify-center text-[0.8rem] font-bold text-brown" aria-hidden="true">PW</div><div><div className="text-[0.85rem] font-semibold text-brown">Piotr W.</div><div className="text-[0.75rem] text-brown-mid">Warszawa · syn Stanisława, 76 l.</div></div></div>
            </div>
            <div className="p-7 bg-cream rounded-xl border border-pageBorder">
              <p className="text-[0.95rem] leading-relaxed text-brown italic mb-5">"Wygenerowaliśmy PDF i wydrukowaliśmy dla całej rodziny na Boże Narodzenie. Babcia płakała że ktoś wreszcie zapisał jej życie."</p>
              <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-cream-dark border border-pageBorder flex items-center justify-center text-[0.8rem] font-bold text-brown" aria-hidden="true">MN</div><div><div className="text-[0.85rem] font-semibold text-brown">Monika N.</div><div className="text-[0.75rem] text-brown-mid">Łódź · wnuczka Ireny, 84 l.</div></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-b border-pageBorder py-24 px-8" id="faq" aria-labelledby="faq-title">
        <div className="max-w-[1100px] mx-auto flex flex-col items-center">
          <div className="w-full text-left md:text-center">
            <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block md:inline-block">Pytania i odpowiedzi</span>
            <h2 id="faq-title" className="font-playfair text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.2] mb-4 text-brown">Najczęściej zadawane pytania</h2>
            <p className="text-[1.05rem] text-brown-mid md:mx-auto max-w-[560px] leading-relaxed">Wszystko o nagrywaniu wspomnień z pomocą AI</p>
          </div>
          
          <div className="mt-12 flex flex-col gap-3 w-full max-w-[800px]">
            {faqs.map((faq, index) => (
              <div key={index} className={`border-[1.5px] rounded-xl overflow-hidden transition-colors ${openFaqIndex === index ? 'border-brown' : 'border-pageBorder hover:border-brown-mid'}`}>
                <button 
                  className="w-full text-left px-6 py-5 bg-transparent border-none cursor-pointer flex justify-between items-center font-lora text-base font-semibold text-brown gap-4 hover:bg-cream"
                  aria-expanded={openFaqIndex === index}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.q}</span>
                  <span className={`text-xl text-gold transition-transform leading-none shrink-0 ${openFaqIndex === index ? 'rotate-45' : ''}`} aria-hidden="true">+</span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-5 text-[0.95rem] text-brown-mid leading-relaxed block" role="region">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture */}
      <section className="bg-cream-dark border-t border-pageBorder py-24 px-8" id="zapis" aria-labelledby="lead-title">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center md:text-left">
             <span className="text-xs font-bold tracking-widest uppercase text-gold mb-4 block">Lista oczekujących</span>
          </div>
          <div className="bg-white rounded-[20px] border-2 border-pageBorder p-8 md:p-12 max-w-[600px] mx-auto mt-12 shadow-sm">
            <div className="inline-block bg-brown text-amber text-[0.75rem] font-bold tracking-widest uppercase py-1.5 px-4 rounded-full mb-6">🎁 OFERTA LIMITOWANA</div>
            <h2 id="lead-title" className="font-playfair text-[1.75rem] font-bold mb-4 text-brown">Dołącz do listy oczekujących</h2>
            <p className="text-brown-mid mb-5">Zapisz się i odbierz:</p>
            
            <ul className="bg-cream border border-pageBorder rounded-xl p-5 mb-7 list-none flex flex-col gap-2.5">
              <li className="flex items-start gap-3 text-[0.9rem] text-brown-mid"><span className="text-gold font-bold shrink-0">✓</span><span><strong>-20% rabatu</strong> na pierwszy pakiet (kod: RODZINA20)</span></li>
              <li className="flex items-start gap-3 text-[0.9rem] text-brown-mid"><span className="text-gold font-bold shrink-0">✓</span><span>Darmowy PDF: <strong>"10 pytań, które odmienią rozmowę z dziadkami"</strong></span></li>
              <li className="flex items-start gap-3 text-[0.9rem] text-brown-mid"><span className="text-gold font-bold shrink-0">✓</span><span>Pierwszeństwo w dostępie do nowych funkcji</span></li>
            </ul>

            {!isSuccess ? (
              <form className="flex flex-col gap-3.5" onSubmit={handleFormSubmit} aria-label="Formularz zapisu">
                <label htmlFor="leadEmail" className="text-[0.875rem] font-semibold text-brown-mid">Twój adres email</label>
                <input 
                  type="email" 
                  id="leadEmail" 
                  name="email" 
                  required 
                  placeholder="np. anna.kowalska@gmail.com" 
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3.5 border-2 border-pageBorder rounded-lg font-lora text-base outline-none transition-colors focus:border-brown bg-white"
                />
                {formError && (
                  <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-3 text-[0.875rem] text-[#B91C1C]" role="alert">{formError}</div>
                )}
                <button type="submit" disabled={isSubmitting} className="bg-brown hover:bg-brown-light text-cream p-4 border-none rounded-lg font-lora text-base font-semibold cursor-pointer transition-all w-full disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Zapisywanie...' : 'Zapisz mnie i wyślij bonusy'}
                </button>
                <p className="text-[0.75rem] text-brown-mid text-center italic mt-1">Szanujemy Twoją prywatność. Możesz wypisać się w każdej chwili.</p>
              </form>
            ) : (
              <div className="text-center py-8" role="status">
                <div className="text-[3rem] mb-4">🎉</div>
                <h3 className="font-playfair text-[1.5rem] mb-3 text-brown font-bold">Dziękujemy!</h3>
                <p className="text-brown-mid">Sprawdź skrzynkę – wysłaliśmy kod <strong className="text-gold">RODZINA20</strong> oraz PDF z pytaniami do dziadków.</p>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-pageBorder text-center">
              <p className="text-[0.85rem] text-brown-mid mb-3">Masz już konto?</p>
              <a href="https://app.opowiedz-mi.pl/register" className="inline-block border-2 border-brown text-brown px-6 py-2.5 rounded-lg text-[0.875rem] font-bold no-underline font-lora transition-colors hover:bg-cream-dark">
                Przejdź do aplikacji →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <div className="bg-brown text-cream text-center py-32 px-8">
        <h2 className="font-playfair text-[clamp(2rem,5vw,3.5rem)] font-bold leading-[1.2] mb-6 text-cream">
          Niektóre pytania można zadać<br /><em className="italic text-amber">tylko teraz</em>
        </h2>
        <p className="text-[1.1rem] text-cream/75 max-w-[520px] mx-auto mb-10 leading-relaxed">
          Za kilka lat możesz żałować że nie nagrałeś tej rozmowy. Pierwsza sesja jest bezpłatna – zajmuje 3 minuty żeby zacząć.
        </p>
        <a href="https://app.opowiedz-mi.pl/register" className="inline-flex items-center gap-2 bg-amber hover:bg-[#FDE68A] text-brown px-10 py-4 rounded-xl font-lora text-[1.05rem] font-semibold transition-colors">
          Nagraj pierwszą rozmowę →
        </a>
        <p className="text-[0.8rem] text-cream/50 mt-4 italic">Bez karty kredytowej · Tomasz dzwoni sam · Po polsku</p>
      </div>

      {/* Footer */}
      <footer className="bg-brown border-t border-white/5 py-12 px-8">
        <div className="max-w-[1100px] mx-auto flex justify-between items-center flex-wrap gap-4 mb-8">
          <div className="font-playfair text-[1.1rem] text-cream font-bold">Opowiedz<span className="text-amber">Mi</span></div>
          <nav className="flex gap-8" aria-label="Linki pomocnicze">
            <a href="mailto:kontakt@opowiedz-mi.pl" className="text-[0.85rem] text-cream/50 hover:text-cream transition-colors">Kontakt</a>
            <a href="/polityka-prywatnosci" className="text-[0.85rem] text-cream/50 hover:text-cream transition-colors">Polityka prywatności</a>
            <a href="/regulamin" className="text-[0.85rem] text-cream/50 hover:text-cream transition-colors">Regulamin</a>
          </nav>
        </div>
        <div className="max-w-[1100px] mx-auto border-t border-white/5 pt-8 text-center">
          <p className="text-[0.8rem] text-cream/35">© 2026 Opowiedz Mi · Zachowujemy rodzinne historie, zanim będzie za późno.</p>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;